import { Heading, Image, Paragraph } from '@zillow/constellation-10';
import React from 'react';

/** A single MLS / Canadian board record from the SSID API. */
export interface MlsData {
    /** Syndicated source ID, used as the section anchor */
    ssid: string;
    /** Full MLS name, rendered as the heading and the logo's alt text */
    longName: string;
    /** Disclaimer copy; when absent the record renders nothing */
    disclaimer?: string;
    /** Photo ID of the MLS logo on photos.zillowstatic.com */
    logo?: string;
}

export interface MlsDisclaimerProps {
    /** The MLS record to render */
    mls: MlsData;
    /** Index in the rendered list; the first one loads its logo eagerly */
    i: number;
}

const MlsDisclaimer: React.FC<MlsDisclaimerProps> = ({ mls, i }) => {
    if (!mls.disclaimer) {
        return null;
    }

    return (
        <section id={`${mls.ssid}`}>
            {/*
             * v8 fontType={{ default: 'h5', sm_lte: 'h6' }}. `sm_lte` is
             * `max-width: 480px`, so h6 applied up to 480px and h5 above it;
             * mobile-first inverts that. Both sizes map exactly: h6 is
             * 16px/24px/700 = body-bold, h5 is 20px/24px/900 = heading-xs.
             * `body-bold` is not in the Heading `textStyle` prop's union (which
             * stops at body-lg-bold), so it goes through `css`, which accepts
             * any textStyle token.
             *
             * The font family is not a translation of a prop that was here — v8
             * Heading derived it from `level` (defaultFontFamily[`h${level}`]),
             * and h2 is theme-dependent: `sansSerif` on ThemeConstellation but
             * `serif` on ThemeSkunkWorks. `fonts.heading` tracks both (Open Sans
             * on legacy-zillow, Object Sans on zillow); `heading-alt` would be
             * Ivar Headline on legacy. It still has to be stated, because the
             * base `body-bold` textStyle otherwise supplies `fonts.default`
             * (Inter on zillow).
             *
             * marginBottom sm = 16px → `default`.
             */}
            <Heading
                level={2}
                fontFamily="heading"
                css={{
                    textStyle: { base: 'body-bold', md: 'heading-xs' },
                    marginBottom: 'default',
                }}
            >
                {mls.longName}
            </Heading>
            {mls.logo && (
                /*
                 * v8 Image's only styling was spacingMixin, so marginBottom
                 * moves into `css` (sm = 16px → `default`). width/height/
                 * loading/decoding/alt are plain img attributes and pass
                 * straight through.
                 *
                 * Delta: v10 Image adds `display: block`, where v8's img stayed
                 * inline. That removes the inline baseline descender gap under
                 * the logo, so the image sits a few px closer to the disclaimer
                 * text. Intrinsic to the v10 component, left as-is.
                 */
                <Image
                    src={`https://photos.zillowstatic.com/fp/${mls.logo}-full.png`}
                    width="70"
                    height="auto"
                    loading={i === 0 ? 'eager' : 'lazy'}
                    decoding={i === 0 ? 'sync' : 'async'}
                    css={{ marginBottom: 'default' }}
                    alt={mls.longName}
                />
            )}
            {/*
             * v8 fontType="finePrint" is 12px/16px/400 → `body-xs`, an exact
             * match. NOT v10 `fineprint`, which is 10px — that is v8's `legal`.
             * (The canonical TYPOGRAPHY_MAP in zrs-cms agrees.)
             * whiteSpace moves from an inline style into `css`; v8 had it inline
             * only because Paragraph had no such prop.
             */}
            <Paragraph textStyle="body-xs" css={{ whiteSpace: 'pre-line' }}>
                {mls.disclaimer}
            </Paragraph>
        </section>
    );
};

export default MlsDisclaimer;
